import {
  BackwardOutlined,
  CloseOutlined,
  ReloadOutlined,
  SaveFilled
} from "@ant-design/icons"
import { App, Button, Empty, Flex, Form, Space } from "antd"
import React, {
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState
} from "react"

import { FormChangedWatcher } from "../../../components/Forms/FormChangedWatcher/FormChangedWatcher.tsx"
import {
  getBulkColumnChangeFields,
  getBulkFeaturesChangeFields,
  getValuesChangeFields
} from "../helpers/cascade.ts"
import type { NestedValues } from "../helpers/dependencies.ts"
import { type OnBulkChange, type OnFeaturesToggle } from "../types.ts"
import type { PermissionFeatures } from "../types/permissions.types.ts"
import { PermissionsContent } from "./PermissionsContent.tsx"

export interface PermissionsFormRef {
  getIsDirty: () => boolean
}

interface PermissionsFormProps {
  permissions: PermissionFeatures | null
  containerClassName?: string
  saving?: boolean
  disabled?: boolean
  isSystem?: boolean
  onTransferOwnership?: () => void
  getDefaultPermissions?: () => Promise<PermissionFeatures | undefined>
  formRef?: Ref<PermissionsFormRef>
  onFinish?: (values: PermissionFeatures) => void
}

export function PermissionsForm({
  saving,
  disabled,
  isSystem,
  onTransferOwnership,
  getDefaultPermissions,
  containerClassName,
  permissions,
  formRef,
  onFinish
}: PermissionsFormProps) {
  const [form] = Form.useForm()
  const [isDirty, setIsDirty] = useState(false)
  const { modal, notification } = App.useApp()

  useImperativeHandle<PermissionsFormRef, PermissionsFormRef>(formRef, () => ({
    getIsDirty: () => isDirty
  }))

  const onBulkColumnChange: OnBulkChange = useCallback(
    (parentKey, colKey, subKeys, value) => {
      const allValues = form.getFieldsValue(true) as NestedValues
      const fields = getBulkColumnChangeFields(
        parentKey as string,
        colKey,
        subKeys,
        value,
        allValues
      )
      if (fields.length > 0) form.setFields(fields)
    },
    [form]
  )

  const onFeaturesToggleChange: OnFeaturesToggle = useCallback(
    (parentKey, features, value) => {
      const allValues = form.getFieldsValue(true) as NestedValues
      const fields = getBulkFeaturesChangeFields(
        parentKey as string,
        features,
        value,
        allValues
      )
      if (fields.length > 0) form.setFields(fields)
    },
    [form]
  )

  const handleValuesChange = useCallback(
    (changedValues: Partial<PermissionFeatures>) => {
      const allValues = form.getFieldsValue(true) as NestedValues
      const fields = getValuesChangeFields(changedValues, allValues)
      if (fields.length > 0) form.setFields(fields)
    },
    [form]
  )

  const handleResetToDefaultConfirm = useCallback(async () => {
    if (!getDefaultPermissions) return

    modal.confirm({
      title: "Load default permissions?",
      content:
        "This will load the system default permission set into the form. You can review and modify them before saving. No changes will be applied until you confirm.",
      okText: "Load defaults",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const defaults = await getDefaultPermissions()
          if (defaults) {
            form.setFieldsValue(defaults)
            notification.success({
              message: "Default permissions loaded",
              description: "Default permissions have been loaded successfully."
            })
            form.submit()
          }
        } catch (e) {
          console.error(e)
        }
      }
    })
  }, [getDefaultPermissions, modal, form])

  useEffect(() => {
    if (permissions) {
      form.setFieldsValue(permissions)
    }
  }, [permissions])

  return (
    <Form
      form={form}
      layout="vertical"
      component={false}
      onFinish={onFinish}
      onValuesChange={handleValuesChange}
      disabled={saving || disabled}
    >
      <FormChangedWatcher
        onDirtyChange={setIsDirty}
        form={form}
        saving={saving}
      />

      <div className={"overflow-auto tailwind-scroll"}>
        {permissions ? (
          <PermissionsContent
            asForm
            disabled={disabled}
            permissions={permissions}
            onBulkChange={onBulkColumnChange}
            onFeaturesToggle={onFeaturesToggleChange}
            isUpdating={saving}
            containerClassName={containerClassName}
          />
        ) : (
          <Empty description="No permissions found" />
        )}
      </div>
      <Flex
        wrap
        gap={8}
        justify={onTransferOwnership ? "space-between" : "end"}
        align="center"
      >
        {onTransferOwnership && (
          <Button
            disabled={false}
            type={"primary"}
            icon={<BackwardOutlined />}
            onClick={onTransferOwnership}
          >
            Back to Transfer Ownership
          </Button>
        )}
        <Flex
          wrap
          justify={isSystem ? "space-between" : "end"}
          gap={8}
          className={"flex-1"}
        >
          {isSystem && (
            <Button
              icon={<ReloadOutlined />}
              danger
              disabled={saving || disabled}
              onClick={handleResetToDefaultConfirm}
            >
              Reset to Default
            </Button>
          )}
          <Space>
            <Button
              onClick={() => form.resetFields()}
              disabled={!isDirty}
              icon={<CloseOutlined />}
              variant={"solid"}
            >
              Cancel Changes
            </Button>
            <Button
              disabled={!isDirty}
              onClick={form.submit}
              icon={<SaveFilled />}
              variant={"solid"}
              color={"primary"}
            >
              Save Changes
            </Button>
          </Space>
        </Flex>
      </Flex>
    </Form>
  )
}
